import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Clock, User, ChevronRight, Send, Loader, BookOpen,
  Zap, ArrowLeft, ChevronDown, BarChart2, FileText, Save, Bot
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { listTeacherStudents, getStudentDetail, sendConceptMessage, getTeacherSubmission, saveSubmissionComment, getStudentAiSessions, getStudentAiSession } from '../../services/studioApi';
import './Studio.css';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function MasteryBar({ score }) {
  const pct = Math.round((score || 0) * 100);
  const color = pct >= 80 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.75rem', color, fontWeight: 700, minWidth: 30 }}>{pct}%</span>
    </div>
  );
}

// ── Student Detail View ────────────────────────────────────────────────────────
// ── Submission detail (expandable inside Activity tab) ────────────────────────
function SubmissionDetail({ submissionId, studentId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTeacherSubmission(submissionId)
      .then(d => { setDetail(d); setComment(d.teacher_comment || ''); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [submissionId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSubmissionComment(submissionId, comment);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: '1rem', textAlign: 'center' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>;
  if (!detail) return null;

  const pct = detail.max_score > 0 ? Math.round((detail.total_score / detail.max_score) * 100) : 0;
  const scoreColor = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#f44336';

  return (
    <div style={{ padding: '1rem 1.1rem', borderTop: '1px solid #eef0f8', background: '#fafbff' }}>
      {/* Score header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.8rem', color: '#888' }}>{detail.questions.length} question{detail.questions.length !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: '1rem', fontWeight: 800, color: scoreColor }}>{pct}% &nbsp;·&nbsp; {detail.total_score?.toFixed(1)}/{detail.max_score?.toFixed(1)}</span>
      </div>

      {/* Questions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {detail.questions.map((q, i) => {
          const correct = q.is_correct;
          return (
            <div key={q.id} style={{ border: `1px solid ${correct ? '#c8e6c9' : '#ffcdd2'}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '0.6rem 0.85rem', background: correct ? '#f1f8f1' : '#fff5f5', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', flexShrink: 0, marginTop: 2 }}>Q{i + 1}</span>
                <span style={{ fontSize: '0.84rem', color: '#1a1a2e', lineHeight: 1.4, flex: 1 }}>{q.text}</span>
                <span style={{ flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, color: correct ? '#4caf50' : '#f44336' }}>
                  {correct ? '✓' : '✗'} {Math.round((q.score || 0) * 100)}%
                </span>
              </div>
              <div style={{ padding: '0.5rem 0.85rem', background: '#fff', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', flexShrink: 0 }}>Student:</span>
                  <span style={{ fontSize: '0.82rem', color: correct ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{q.student_answer || '—'}</span>
                </div>
                {!correct && (q.correct_answer || q.model_answer) && (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', flexShrink: 0 }}>Correct:</span>
                    <span style={{ fontSize: '0.82rem', color: '#1a5c2a' }}>{q.correct_answer || q.model_answer}</span>
                  </div>
                )}
                {q.ai_feedback && (
                  <div style={{ fontSize: '0.78rem', color: '#666', fontStyle: 'italic', borderTop: '1px solid #f0f0f0', paddingTop: '0.3rem', marginTop: '0.1rem' }}>
                    {q.ai_feedback}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Teacher comment */}
      <div style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '0.75rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#5B35A0', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Teacher feedback to student
        </div>
        <textarea
          className="form-textarea"
          rows={3}
          style={{ width: '100%', fontSize: '0.85rem', resize: 'vertical', borderRadius: 8, padding: '0.5rem 0.75rem', boxSizing: 'border-box' }}
          placeholder="Write feedback for this submission…"
          value={comment}
          onChange={e => setComment(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: saved ? '#4caf50' : '#667eea', color: '#fff', fontSize: '0.82rem', fontWeight: 600, transition: 'background 0.2s' }}
          >
            {saving ? <Loader size={13} className="spin" /> : <Save size={13} />}
            {saved ? 'Saved!' : 'Save Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 4-tab student detail view ─────────────────────────────────────────────────
function StudentDetailView({ studentId, onBack }) {
  const { language } = useLanguage();
  const tT = (translations[language]?.studio?.teacher) || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('activity');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [collapsedCourses, setCollapsedCourses] = useState({});
  const [replyInput, setReplyInput] = useState({});
  const [sending, setSending] = useState({});
  const [activeThread, setActiveThread] = useState(null);
  const [aiSessions, setAiSessions] = useState([]);
  const [aiSessionsLoading, setAiSessionsLoading] = useState(false);
  const [expandedAiSession, setExpandedAiSession] = useState(null);
  const [aiSessionDetail, setAiSessionDetail] = useState({});
  const [aiSessionDetailLoading, setAiSessionDetailLoading] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getStudentDetail(studentId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => {
    if (tab === 'aichats' && aiSessions.length === 0 && !aiSessionsLoading) {
      setAiSessionsLoading(true);
      getStudentAiSessions(studentId)
        .then(setAiSessions)
        .catch(console.error)
        .finally(() => setAiSessionsLoading(false));
    }
  }, [tab, studentId, aiSessions.length, aiSessionsLoading]);

  const handleExpandAiSession = async (sessionId) => {
    if (expandedAiSession === sessionId) { setExpandedAiSession(null); return; }
    setExpandedAiSession(sessionId);
    if (aiSessionDetail[sessionId]) return;
    setAiSessionDetailLoading(l => ({ ...l, [sessionId]: true }));
    try {
      const detail = await getStudentAiSession(studentId, sessionId);
      setAiSessionDetail(d => ({ ...d, [sessionId]: detail }));
    } catch (e) { console.error(e); }
    finally { setAiSessionDetailLoading(l => ({ ...l, [sessionId]: false })); }
  };

  useEffect(() => { load(); }, [load]);

  const handleReply = async (conceptId) => {
    const body = (replyInput[conceptId] || '').trim();
    if (!body) return;
    setSending(s => ({ ...s, [conceptId]: true }));
    try {
      await sendConceptMessage(conceptId, body, studentId);
      setReplyInput(r => ({ ...r, [conceptId]: '' }));
      load();
    } catch (e) { console.error(e); }
    finally { setSending(s => ({ ...s, [conceptId]: false })); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!data) return null;

  const { student, practice, submissions, messages, mastery } = data;

  const threadMap = {};
  messages.forEach(m => {
    const key = String(m.concept_id);
    if (!threadMap[key]) threadMap[key] = { title: m.concept_title, messages: [] };
    threadMap[key].messages.push(m);
  });

  const practiceTimeline = [...practice]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Group submissions by course title
  const submissionsByCourse = {};
  submissions.forEach(s => {
    const key = s.course_title || 'General';
    if (!submissionsByCourse[key]) submissionsByCourse[key] = [];
    submissionsByCourse[key].push(s);
  });

  const avgMastery = mastery.length ? Math.round(mastery.reduce((a, m) => a + (m.mastery_score || 0), 0) / mastery.length * 100) : 0;
  const masteryColor = avgMastery >= 80 ? '#4caf50' : avgMastery >= 50 ? '#ff9800' : '#667eea';

  const tabs = [
    { key: 'activity', label: tT.activityTab || 'Activity', icon: <Zap size={14} /> },
    { key: 'conversations', label: tT.conversationsTab || 'Conversations', icon: <MessageCircle size={14} />, badge: Object.keys(threadMap).length },
    { key: 'mastery', label: tT.masteryTab || 'Mastery', icon: <BarChart2 size={14} /> },
    { key: 'aichats', label: tT.aiChatsTab || 'AI Chats', icon: <Bot size={14} /> },
  ];

  return (
    <div>
      {/* Header */}
      <button className="studio-back-btn" onClick={onBack} style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={15} /> {tT.allStudentsBack || 'All students'}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem', background: 'linear-gradient(135deg, #5B35A0 0%, #2D1B5E 100%)', borderRadius: 14, marginBottom: '1.25rem', color: '#fff' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <User size={22} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{student.full_name}</div>
          <div style={{ fontSize: '0.82rem', opacity: 0.75 }}>{student.email}</div>
        </div>
        <div style={{ display: 'flex', gap: '1.25rem', flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: masteryColor }}>{avgMastery}%</div>
            <div style={{ fontSize: '0.68rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{tT.masteryLabel || 'Mastery'}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{practice.length}</div>
            <div style={{ fontSize: '0.68rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{tT.sessions || 'Sessions'}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{submissions.length}</div>
            <div style={{ fontSize: '0.68rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{tT.submittedCount || 'Submitted'}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '2px solid #eef0f8', marginBottom: '1.25rem' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            padding: '0.55rem 1rem', border: 'none', cursor: 'pointer',
            background: 'none', fontSize: '0.85rem', fontWeight: tab === t.key ? 700 : 500,
            color: tab === t.key ? '#5B35A0' : '#888',
            borderBottom: `2px solid ${tab === t.key ? '#5B35A0' : 'transparent'}`,
            marginBottom: -2, transition: 'color 0.15s',
          }}>
            {t.icon} {t.label}
            {t.badge > 0 && <span style={{ background: '#667eea', color: '#fff', borderRadius: 10, padding: '0 5px', fontSize: '0.65rem', fontWeight: 700 }}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── Activity Tab ── */}
      {tab === 'activity' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {submissions.length === 0 && practiceTimeline.length === 0 && (
            <p style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{tT.noActivity || 'No activity yet.'}</p>
          )}

          {/* Assignments grouped by course */}
          {Object.keys(submissionsByCourse).length > 0 && Object.entries(submissionsByCourse).map(([course, subs]) => {
            const collapsed = collapsedCourses[course];
            const avgScore = subs.length ? Math.round(subs.reduce((a, s) => a + (s.score_percentage || 0), 0) / subs.length) : 0;
            return (
              <div key={course} style={{ border: '1px solid #e8ecf0', borderRadius: 14, overflow: 'hidden' }}>
                {/* Course header */}
                <button
                  onClick={() => setCollapsedCourses(c => ({ ...c, [course]: !c[course] }))}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: '#f8f4ff', border: 'none', cursor: 'pointer' }}
                >
                  <BookOpen size={14} color="#5B35A0" />
                  <span style={{ flex: 1, fontWeight: 700, fontSize: '0.87rem', color: '#5B35A0', textAlign: 'left' }}>{course}</span>
                  <span style={{ fontSize: '0.72rem', color: '#888', background: '#ede9fe', borderRadius: 8, padding: '2px 7px' }}>{subs.length} {subs.length !== 1 ? (tT.submissionPlural || 'submissions') : (tT.submissionSingular || 'submission')}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: avgScore >= 70 ? '#4caf50' : '#f44336' }}>{avgScore}% {tT.avgShort || 'avg'}</span>
                  <ChevronDown size={14} color="#5B35A0" style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
                </button>

                {!collapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {subs.map((s, i) => (
                      <div key={s.submission_id} style={{ borderTop: i > 0 ? '1px solid #f0f0f0' : '1px solid #eef0f8' }}>
                        <div
                          style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.7rem 1rem', cursor: 'pointer', background: expandedSubmission === s.submission_id ? '#faf8ff' : '#fff' }}
                          onClick={() => setExpandedSubmission(id => id === s.submission_id ? null : s.submission_id)}
                        >
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FileText size={13} color="#4caf50" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.assignment_title}</div>
                            <div style={{ fontSize: '0.73rem', color: '#aaa', display: 'flex', gap: '0.4rem' }}>
                              {timeAgo(s.submitted_at)}
                              {s.teacher_comment && <span style={{ color: '#667eea', fontWeight: 600 }}>· Feedback given</span>}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: (s.score_percentage || 0) >= 70 ? '#4caf50' : '#f44336' }}>{Math.round(s.score_percentage || 0)}%</span>
                            <ChevronDown size={14} color="#aaa" style={{ transform: expandedSubmission === s.submission_id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                          </div>
                        </div>
                        {expandedSubmission === s.submission_id && (
                          <SubmissionDetail submissionId={s.submission_id} studentId={studentId} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Practice sessions flat list */}
          {practiceTimeline.length > 0 && (
            <div style={{ border: '1px solid #e8ecf0', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: '#f0f4ff' }}>
                <Zap size={14} color="#667eea" />
                <span style={{ fontWeight: 700, fontSize: '0.87rem', color: '#667eea' }}>{tT.practiceSessionsLabel || 'Practice Sessions'}</span>
                <span style={{ fontSize: '0.72rem', color: '#888', background: '#e8edff', borderRadius: 8, padding: '2px 7px', marginLeft: 'auto' }}>{practiceTimeline.length} session{practiceTimeline.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {practiceTimeline.map((p, i) => (
                  <div key={p.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.65rem 1rem', borderTop: i > 0 ? '1px solid #f5f5f5' : '1px solid #eef0f8', background: '#fff' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.concept_title}</div>
                      <div style={{ fontSize: '0.73rem', color: '#aaa' }}>{p.correct_count}/{p.questions_asked} correct · {timeAgo(p.created_at)}</div>
                    </div>
                    <span style={{ fontSize: '0.84rem', fontWeight: 700, color: (p.score_pct || 0) >= 70 ? '#4caf50' : '#f44336', flexShrink: 0 }}>{Math.round(p.score_pct || 0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Conversations Tab ── */}
      {tab === 'conversations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {Object.keys(threadMap).length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{tT.noMessages || 'No messages yet.'}</p>}
          {Object.entries(threadMap).map(([conceptId, thread]) => (
            <div key={conceptId} style={{ border: '1px solid #e8ecf0', borderRadius: 12, overflow: 'hidden' }}>
              <button
                onClick={() => setActiveThread(t => t === conceptId ? null : conceptId)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: activeThread === conceptId ? '#f3eeff' : '#fff', border: 'none', cursor: 'pointer' }}
              >
                <BookOpen size={14} color="#5B35A0" />
                <span style={{ flex: 1, fontWeight: 600, fontSize: '0.87rem', color: '#1a1a2e', textAlign: 'left' }}>{thread.title}</span>
                <span style={{ fontSize: '0.72rem', color: '#888', background: '#f0f0f0', borderRadius: 8, padding: '2px 7px' }}>{thread.messages.length} {tT.msgShort || 'msg'}</span>
                <ChevronDown size={14} color="#aaa" style={{ transform: activeThread === conceptId ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
              </button>
              {activeThread === conceptId && (
                <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #f0f0f0', background: '#fafbff' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {thread.messages.map(m => (
                      <div key={m.id} style={{
                        alignSelf: m.sender === 'teacher' ? 'flex-end' : 'flex-start',
                        maxWidth: '80%', padding: '0.5rem 0.85rem', borderRadius: 12,
                        background: m.sender === 'teacher' ? '#667eea' : '#f3f0ff',
                        color: m.sender === 'teacher' ? '#fff' : '#1a1a2e',
                        fontSize: '0.85rem', lineHeight: 1.5,
                      }}>
                        {m.body}
                        <div style={{ fontSize: '0.67rem', opacity: 0.6, marginTop: '0.2rem', textAlign: 'right' }}>{timeAgo(m.created_at)}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      className="form-input"
                      style={{ flex: 1, fontSize: '0.85rem', padding: '0.5rem 0.75rem', minWidth: 0 }}
                      placeholder={tT.replyPlaceholder || 'Reply to student…'}
                      value={replyInput[conceptId] || ''}
                      onChange={e => setReplyInput(r => ({ ...r, [conceptId]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleReply(conceptId)}
                    />
                    <button
                      onClick={() => handleReply(conceptId)}
                      disabled={sending[conceptId] || !replyInput[conceptId]?.trim()}
                      style={{
                        flexShrink: 0, width: 38, height: 38, borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: replyInput[conceptId]?.trim() ? '#667eea' : '#e8ecf0',
                        color: replyInput[conceptId]?.trim() ? '#fff' : '#aaa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s',
                      }}
                    >
                      {sending[conceptId] ? <Loader size={14} className="spin" /> : <Send size={14} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Mastery Tab ── */}
      {tab === 'mastery' && (
        <div>
          {mastery.length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{tT.noMasteryData || 'No mastery data yet.'}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[...mastery].sort((a, b) => (a.mastery_score || 0) - (b.mastery_score || 0)).map(m => {
              const pct = Math.round((m.mastery_score || 0) * 100);
              const color = pct >= 80 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336';
              return (
                <div key={m.concept_id} style={{ background: '#fff', border: '1px solid #eef0f8', borderRadius: 10, padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                    <span style={{ fontSize: '0.87rem', fontWeight: 600, color: '#1a1a2e' }}>{m.title}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color }}>{pct}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.3rem' }}>
                    {m.attempts || 0} {m.attempts !== 1 ? (tT.attemptPlural || 'attempts') : (tT.attemptSingular || 'attempt')} · {m.trend || (tT.newBadge || 'new')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── AI Chats Tab ── */}
      {tab === 'aichats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ background: '#f0f4ff', border: '1px solid #dde4ff', borderRadius: 10, padding: '0.65rem 1rem', fontSize: '0.8rem', color: '#5B35A0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Bot size={14} />
            {tT.aiChatsDesc || 'These are AI conversations this student had while studying your course content.'}
          </div>
          {aiSessionsLoading && <div style={{ textAlign: 'center', padding: '2rem 0' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>}
          {!aiSessionsLoading && aiSessions.length === 0 && (
            <p style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{tT.noAiSessions || 'No AI chat sessions yet.'}</p>
          )}
          {aiSessions.map(s => {
            const isOpen = expandedAiSession === s.id;
            const detail = aiSessionDetail[s.id];
            const detailLoading = aiSessionDetailLoading[s.id];
            return (
              <div key={s.id} style={{ border: '1px solid #e8ecf0', borderRadius: 12, overflow: 'hidden' }}>
                <button
                  onClick={() => handleExpandAiSession(s.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: isOpen ? '#f3eeff' : '#fff', border: 'none', cursor: 'pointer' }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Bot size={14} color="#5B35A0" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.concept_title}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#aaa' }}>{s.course_title} · {timeAgo(s.updated_at)}</div>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#888', background: '#f0f0f0', borderRadius: 8, padding: '2px 7px', flexShrink: 0 }}>{s.message_count} {tT.msgShort || 'msg'}</span>
                  <ChevronDown size={14} color="#aaa" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                </button>
                {isOpen && (
                  <div style={{ borderTop: '1px solid #f0f0f0', background: '#fafbff', padding: '0.75rem 1rem' }}>
                    {detailLoading && <div style={{ textAlign: 'center', padding: '1rem 0' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>}
                    {!detailLoading && detail && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {detail.messages.map(m => (
                          <div key={m.id} style={{
                            alignSelf: m.role === 'assistant' ? 'flex-start' : 'flex-end',
                            maxWidth: '82%', padding: '0.5rem 0.85rem', borderRadius: 12,
                            background: m.role === 'assistant' ? '#f3f0ff' : '#667eea',
                            color: m.role === 'assistant' ? '#1a1a2e' : '#fff',
                            fontSize: '0.84rem', lineHeight: 1.5,
                          }}>
                            <div style={{ fontSize: '0.67rem', fontWeight: 700, opacity: 0.65, marginBottom: '0.15rem' }}>
                              {m.role === 'assistant' ? (tT.aiLabel || 'AI') : (tT.studentLabel || 'Student')}
                            </div>
                            {m.content}
                            <div style={{ fontSize: '0.65rem', opacity: 0.55, marginTop: '0.2rem', textAlign: 'right' }}>{timeAgo(m.created_at)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Student Roster tab content (used inside TeacherDashboard) ─────────────────
export function StudentRosterTab() {
  const { language } = useLanguage();
  const tT = (translations[language]?.studio?.teacher) || {};

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listTeacherStudents()
      .then(setStudents)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selectedStudent) {
    return <StudentDetailView studentId={selectedStudent} onBack={() => { setSelectedStudent(null); load(); }} />;
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (students.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><User size={48} /></div>
        <h3>{tT.noStudentsRoster || 'No students yet'}</h3>
        <p>{tT.studentsEnrollNote || 'Students will appear here once they enrol in your classes.'}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {students.map(s => (
        <button
          key={s.user_id}
          onClick={() => setSelectedStudent(s.user_id)}
          style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 14, padding: '1rem 1.25rem', cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(102,126,234,0.12)'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={18} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a1a2e' }}>{s.full_name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                  {s.unread_count > 0 && (
                    <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700 }}>
                      {s.unread_count} {tT.newBadge || 'new'}
                    </span>
                  )}
                  <span style={{ fontSize: '0.75rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                    <Clock size={11} /> {timeAgo(s.last_active)}
                  </span>
                  <ChevronRight size={14} color="#ccc" />
                </div>
              </div>
              <div style={{ marginBottom: '0.35rem' }}>
                <MasteryBar score={s.avg_mastery} />
              </div>
              {s.last_message && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.77rem', color: '#888', overflow: 'hidden' }}>
                  <MessageCircle size={11} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.last_message.sender === 'student' ? '' : 'You: '}
                    {s.last_message.body}
                  </span>
                </div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Standalone page (redirects to teacher dashboard students tab) ─────────────
export default function StudentRoster() {
  const navigate = useNavigate();
  useEffect(() => { navigate('/studio/teacher?tab=students', { replace: true }); }, [navigate]);
  return null;
}
